import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { FocusEvent, useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";
import { Link as RouterLink } from "react-router-dom";
import { BrandInputPartial } from "../../../../mutations/__generated__/useBrandUpdateMutation.graphql";
import {
  SocialLoginCard_brand$data,
  SocialLoginCard_brand$key,
} from "./__generated__/SocialLoginCard_brand.graphql";
import CardSection from "../../../../components/CardSection";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import CopyButton from "../../../../components/CopyButton";
import LeftRight from "../../../../components/LeftRight";
import RighthandDrawer from "../../../../components/RighthandDrawer";
import SaveButton, { SavedState } from "../../../../components/SaveButton";
import useBrandUpdateMutation from "../../../../mutations/useBrandUpdateMutation";
import {
  SAVED_SECRET_MASK,
  SAVED_SECRET_PLACEHOLDER,
} from "../../../../utils/constants";
import { SETTINGS_SHOPIFY } from "../../../../utils/routes";

type CredentialField = {
  label: string;
  // Readable fields expose their value; write-only secrets only whether one is saved.
  value?: (brand: SocialLoginCard_brand$data) => string;
  isSecretSet?: (brand: SocialLoginCard_brand$data) => boolean;
  saveValue: (
    brand: SocialLoginCard_brand$data,
    value: string
  ) => BrandInputPartial;
};

type SocialLoginProvider = {
  key: "facebook" | "google";
  name: "Facebook" | "Google";
  description: string;
  enabled: boolean;
  hasCredentials: boolean;
  credentialValue: string;
  fields: CredentialField[];
  saveEnabled: (enabled: boolean) => BrandInputPartial;
  missingCredentialsBody: string;
};

const isWriteOnly = (field: CredentialField) => field.isSecretSet !== undefined;

const getDisplayValue = (
  field: CredentialField,
  brand: SocialLoginCard_brand$data
) =>
  isWriteOnly(field)
    ? field.isSecretSet!(brand)
      ? SAVED_SECRET_MASK
      : ""
    : field.value?.(brand) ?? "";

const APP_PLATFORM_INFO_FIELDS: {
  label: string;
  value: (brand: SocialLoginCard_brand$data) => string;
}[] = [
  {
    label: "iOS Bundle ID",
    value: (brand) => brand.appConfig.iosAppIdentifier ?? "",
  },
  {
    label: "Android Package Name",
    value: (brand) => brand.appConfig.androidAppIdentifier ?? "",
  },
];

const brandFragment = graphql`
  fragment SocialLoginCard_brand on BrandType {
    ...useBrandUpdateMutation_brand
    facebookAppId
    hasFacebookAppSecret
    facebookClientToken
    facebookLoginEnabled
    googleIosClientId
    googleWebClientId
    googleLoginEnabled
    appConfig {
      id
      iosAppIdentifier
      androidAppIdentifier
    }
    shopifyConfig {
      multipassEnabled
      isNewCustomerAccountsEnabled
    }
  }
`;

const getProviders = (
  brand: SocialLoginCard_brand$data
): SocialLoginProvider[] => {
  const facebookFields: CredentialField[] = [
    {
      label: "App ID",
      value: (currentBrand) => currentBrand.facebookAppId ?? "",
      saveValue: (_currentBrand, value) => ({ facebookAppId: value }),
    },
    {
      label: "Client Token",
      value: (currentBrand) => currentBrand.facebookClientToken ?? "",
      saveValue: (_currentBrand, value) => ({ facebookClientToken: value }),
    },
    {
      label: "App Secret",
      isSecretSet: (currentBrand) => currentBrand.hasFacebookAppSecret,
      saveValue: (_currentBrand, value) => ({ facebookAppSecret: value }),
    },
  ];
  const googleFields: CredentialField[] = [
    {
      label: "iOS OAuth Client ID",
      value: (currentBrand) => currentBrand.googleIosClientId ?? "",
      saveValue: (_currentBrand, value) => ({ googleIosClientId: value }),
    },
    {
      label: "Web OAuth Client ID",
      value: (currentBrand) => currentBrand.googleWebClientId ?? "",
      saveValue: (_currentBrand, value) => ({ googleWebClientId: value }),
    },
  ];

  return [
    {
      key: "facebook",
      name: "Facebook",
      description: "Let customers sign in with Facebook.",
      enabled: brand.facebookLoginEnabled,
      hasCredentials: facebookFields.every(
        (field) => getDisplayValue(field, brand).length > 0
      ),
      credentialValue: facebookFields
        .map((field) => getDisplayValue(field, brand))
        .join(""),
      fields: facebookFields,
      saveEnabled: (enabled) => ({ facebookLoginEnabled: enabled }),
      missingCredentialsBody:
        "Add an App ID, Client Token, and App Secret before Facebook login can appear in the app.",
    },
    {
      key: "google",
      name: "Google",
      description: "Let customers sign in with Google.",
      enabled: brand.googleLoginEnabled,
      hasCredentials: googleFields.every(
        (field) => getDisplayValue(field, brand).length > 0
      ),
      credentialValue: googleFields
        .map((field) => getDisplayValue(field, brand))
        .join(""),
      fields: googleFields,
      saveEnabled: (enabled) => ({ googleLoginEnabled: enabled }),
      missingCredentialsBody:
        "Add an iOS OAuth Client ID and Web OAuth Client ID before Google login can appear in the app.",
    },
  ];
};

const getProviderStatus = (
  provider: SocialLoginProvider,
  {
    hasMultipass,
    hasNewCustomerAccounts,
  }: {
    hasMultipass: boolean;
    hasNewCustomerAccounts: boolean;
  }
) => {
  if (!provider.enabled) {
    return provider.hasCredentials ? null : "Add credentials to turn this on.";
  }
  if (!provider.hasCredentials) {
    return "On, but hidden until credentials are added.";
  }
  if (hasNewCustomerAccounts) {
    return "On, but hidden while Shopify new customer accounts is enabled.";
  }
  if (!hasMultipass) {
    return "On, but hidden until Multipass is configured.";
  }
  return "Visible in the app on supported versions.";
};

const ReadonlyIdentifierField = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  const isEmpty = value.length === 0;
  return (
    <TextField
      fullWidth
      margin="normal"
      label={label}
      variant="outlined"
      value={isEmpty ? "Not set" : value}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <CopyButton textToCopy={value} isDisabled={isEmpty} isIconButton />
          </InputAdornment>
        ),
      }}
    />
  );
};

const SocialLoginCredentialsDrawer = ({
  brand,
  provider,
  isOpen,
  onClose,
  onSaved,
}: {
  brand: SocialLoginCard_brand$data;
  provider: SocialLoginProvider | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (provider: SocialLoginProvider) => void;
}) => {
  const [updateBrand, isBrandMutationInFlight] = useBrandUpdateMutation(brand);
  const [saveButtonState, setSaveButtonState] = useState<SavedState>(
    SavedState.DISABLED
  );
  const [textFieldValues, setTextFieldValues] = useState<string[]>([]);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  // Blank secret inputs keep the saved value, so clearing one needs its own action.
  const savedSecretFields =
    provider?.fields.filter((field) => field.isSecretSet?.(brand)) ?? [];
  const savedSecretLabels = savedSecretFields
    .map((field) => field.label)
    .join(" and ");

  const onCloseDrawer = useCallback(() => {
    onClose();
    setTextFieldValues([]);
    setSaveButtonState(SavedState.DISABLED);
    setIsRemoveDialogOpen(false);
  }, [onClose]);

  useEffect(() => {
    setTextFieldValues(
      provider
        ? provider.fields.map((field) =>
            isWriteOnly(field) ? "" : field.value?.(brand) ?? ""
          )
        : []
    );
  }, [provider, brand]);

  return (
    <RighthandDrawer
      isOpen={isOpen}
      title={provider ? `Edit ${provider.name} Credentials` : ""}
      onClose={onCloseDrawer}
    >
      <Stack
        direction="column"
        sx={{
          padding: 2,
        }}
        spacing={2}
        alignItems={"flex-end"}
      >
        {provider?.fields.map((field, index) => (
          <TextField
            key={field.label}
            fullWidth
            margin="normal"
            label={field.label}
            variant="outlined"
            value={textFieldValues[index] ?? ""}
            placeholder={
              field.isSecretSet?.(brand) ? SAVED_SECRET_PLACEHOLDER : undefined
            }
            InputLabelProps={
              field.isSecretSet?.(brand) ? { shrink: true } : undefined
            }
            onChange={(event: FocusEvent<HTMLInputElement>) => {
              setTextFieldValues((prev) => {
                const newValues = [...prev];
                newValues[index] = event.target.value;
                return newValues;
              });
              saveButtonState !== SavedState.ENABLED &&
                setSaveButtonState(SavedState.ENABLED);
            }}
          />
        ))}
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            Add these identifiers when you configure iOS and Android platforms
            in the{" "}
            {provider?.key === "google"
              ? "Google Cloud Console"
              : "Facebook Developer portal"}
            .
          </Typography>
          {APP_PLATFORM_INFO_FIELDS.map((field) => (
            <ReadonlyIdentifierField
              key={field.label}
              label={field.label}
              value={field.value(brand)}
            />
          ))}
        </Stack>
        {provider !== null && savedSecretFields.length > 0 ? (
          <>
            <Button
              color="error"
              disabled={isBrandMutationInFlight}
              onClick={() => setIsRemoveDialogOpen(true)}
            >
              Remove {savedSecretLabels}
            </Button>
            <ConfirmDialog
              isOpen={isRemoveDialogOpen}
              title={`Remove ${provider.name} ${savedSecretLabels}?`}
              body={`${provider.name} login will be hidden in the app until a new ${savedSecretLabels} is saved.`}
              confirmLabel="Remove"
              isConfirmDisabled={isBrandMutationInFlight}
              onClose={() => setIsRemoveDialogOpen(false)}
              onConfirm={() => {
                const inputPartial = savedSecretFields.reduce(
                  (acc, field) => ({ ...acc, ...field.saveValue(brand, "") }),
                  {} as BrandInputPartial
                );
                updateBrand(inputPartial, (_) => onCloseDrawer());
              }}
            />
          </>
        ) : null}
        <SaveButton
          savedState={
            isBrandMutationInFlight ? SavedState.DISABLED : saveButtonState
          }
          onClick={
            provider == null
              ? undefined
              : () => {
                  const inputPartial = provider.fields.reduce(
                    (acc, field, index) =>
                      // An empty write-only field keeps the saved secret.
                      isWriteOnly(field) && !textFieldValues[index]
                        ? acc
                        : {
                            ...acc,
                            ...field.saveValue(
                              brand,
                              textFieldValues[index] ?? ""
                            ),
                          },
                    {} as BrandInputPartial
                  );
                  updateBrand(inputPartial, (_) => {
                    setSaveButtonState(SavedState.SAVED);
                    onCloseDrawer();
                    onSaved({
                      ...provider,
                      hasCredentials: provider.fields.every(
                        (field, index) =>
                          (textFieldValues[index] ?? "").length > 0 ||
                          Boolean(field.isSecretSet?.(brand))
                      ),
                    });
                  });
                }
          }
        />
      </Stack>
    </RighthandDrawer>
  );
};

const SocialLoginToggleDialog = ({
  provider,
  enable,
  kind,
  hasMultipass,
  hasNewCustomerAccounts,
  isSaving,
  onCancel,
  onConfirm,
  onAddCredentials,
}: {
  provider: SocialLoginProvider;
  enable: boolean;
  kind: "confirm" | "missing-credentials";
  hasMultipass: boolean;
  hasNewCustomerAccounts: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onAddCredentials: () => void;
}) => {
  if (kind === "missing-credentials") {
    return (
      <Dialog open onClose={onCancel} fullWidth>
        <DialogTitle>Add {provider.name} credentials</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {provider.missingCredentialsBody}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button onClick={onAddCredentials} variant="contained">
            Add credentials
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onCancel} fullWidth>
      <DialogTitle>
        {enable
          ? `Turn on ${provider.name} login?`
          : `Turn off ${provider.name} login?`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {enable
              ? `The ${provider.name} button will appear on the app sign-in screen right away for customers on a supported app version. You can turn this off anytime without removing your credentials.`
              : `The ${provider.name} button will be hidden on the app sign-in screen right away. Customers who previously used ${provider.name} can still sign in with email or another method. Your credentials will stay saved.`}
          </DialogContentText>
          {enable && hasNewCustomerAccounts ? (
            <DialogContentText>
              Social login is unavailable while Shopify new customer accounts is
              enabled. The button will stay hidden until that is turned off.
            </DialogContentText>
          ) : null}
          {enable && !hasMultipass ? (
            <DialogContentText>
              Social login also needs a Shopify Multipass secret. Until that is
              added in{" "}
              <Link component={RouterLink} to={SETTINGS_SHOPIFY}>
                Shopify settings
              </Link>
              , this button will stay hidden.
            </DialogContentText>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={enable ? "primary" : "error"}
          disabled={isSaving}
        >
          {enable ? "Turn on" : "Turn off"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SocialLoginProviderRow = ({
  provider,
  status,
  isSaving,
  onEditCredentials,
  onToggle,
}: {
  provider: SocialLoginProvider;
  status: string | null;
  isSaving: boolean;
  onEditCredentials: () => void;
  onToggle: (enabled: boolean) => void;
}) => {
  const isEmpty = provider.credentialValue.length === 0;
  const actions = (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        justifyContent: "flex-end",
        flexShrink: 0,
      }}
    >
      {provider.fields.some(isWriteOnly) ? null : (
        <CopyButton
          textToCopy={provider.credentialValue}
          isDisabled={isEmpty || provider.fields.length > 1}
        />
      )}
      <Button startIcon={<EditIcon />} onClick={onEditCredentials}>
        Edit
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={1.5}>
      <LeftRight
        expandLeft
        left={
          <Stack>
            <Typography
              variant="body2"
              sx={{
                color: "black",
                fontWeight: "bold",
              }}
            >
              {provider.name} Login
            </Typography>
            <Typography variant="body2">{provider.description}</Typography>
            {status ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {status}
              </Typography>
            ) : null}
          </Stack>
        }
        right={
          <Switch
            checked={provider.enabled}
            disabled={isSaving}
            onChange={(_event, checked) => onToggle(checked)}
          />
        }
      />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ width: "100%" }}
      >
        <TextField
          fullWidth
          disabled={isEmpty}
          margin="normal"
          label={`${provider.name} Credentials`}
          variant="outlined"
          value={"•".repeat(provider.credentialValue.length)}
          sx={{ flexGrow: 1, minWidth: 0, mt: { xs: 0, sm: 2 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                {actions}
              </InputAdornment>
            ),
            readOnly: true,
          }}
        />
        <Box sx={{ display: { xs: "block", sm: "none" } }}>{actions}</Box>
      </Stack>
    </Stack>
  );
};

const SocialLoginCard = ({
  brand: brandKey,
}: {
  brand: SocialLoginCard_brand$key;
}) => {
  const brand = useFragment<SocialLoginCard_brand$key>(brandFragment, brandKey);
  const [updateBrand, isBrandMutationInFlight] = useBrandUpdateMutation(brand);
  const [editingProviderKey, setEditingProviderKey] = useState<
    SocialLoginProvider["key"] | null
  >(null);
  const [pendingToggle, setPendingToggle] = useState<{
    key: SocialLoginProvider["key"];
    enable: boolean;
    kind: "confirm" | "missing-credentials";
  } | null>(null);

  const providers = getProviders(brand);
  const editingProvider =
    providers.find((provider) => provider.key === editingProviderKey) ?? null;
  const pendingProvider =
    providers.find((provider) => provider.key === pendingToggle?.key) ?? null;
  const hasMultipass = brand.shopifyConfig?.multipassEnabled ?? false;
  const hasNewCustomerAccounts =
    brand.shopifyConfig?.isNewCustomerAccountsEnabled ?? false;

  const onToggle = useCallback(
    (provider: SocialLoginProvider, enable: boolean) => {
      setPendingToggle({
        key: provider.key,
        enable,
        kind:
          enable && !provider.hasCredentials
            ? "missing-credentials"
            : "confirm",
      });
    },
    []
  );

  const onConfirmToggle = useCallback(() => {
    if (pendingProvider == null || pendingToggle == null) {
      return;
    }
    updateBrand(pendingProvider.saveEnabled(pendingToggle.enable), () => {
      setPendingToggle(null);
    });
  }, [pendingProvider, pendingToggle, updateBrand]);

  const onCredentialsSaved = useCallback((provider: SocialLoginProvider) => {
    if (provider.hasCredentials && !provider.enabled) {
      setPendingToggle({
        key: provider.key,
        enable: true,
        kind: "confirm",
      });
    }
  }, []);

  return (
    <>
      <CardSection
        title="Social Login"
        subtitle="Show Facebook or Google on the app sign-in screen. Changes take effect immediately — no new app build is required."
        content={
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Social login requires Shopify Multipass and cannot be used if the
              store has migrated to Shopify's new customer accounts.{" "}
              <Link component={RouterLink} to={SETTINGS_SHOPIFY}>
                Configure Multipass in Shopify settings
              </Link>
              .
            </Typography>
            {hasNewCustomerAccounts ? (
              <Alert severity="warning">
                This store has migrated to Shopify new customer accounts, so
                social login is unavailable.
              </Alert>
            ) : null}
            {!hasMultipass ? (
              <Alert severity="info">
                Add a Multipass secret in{" "}
                <Link component={RouterLink} to={SETTINGS_SHOPIFY}>
                  Shopify settings
                </Link>{" "}
                before social login can appear in the app.
              </Alert>
            ) : null}
            {providers.map((provider, index) => (
              <Box key={provider.key}>
                {index > 0 ? <Divider sx={{ mb: 3 }} /> : null}
                <SocialLoginProviderRow
                  provider={provider}
                  status={getProviderStatus(provider, {
                    hasMultipass,
                    hasNewCustomerAccounts,
                  })}
                  isSaving={isBrandMutationInFlight}
                  onEditCredentials={() => setEditingProviderKey(provider.key)}
                  onToggle={(enabled) => onToggle(provider, enabled)}
                />
              </Box>
            ))}
          </Stack>
        }
      />
      <SocialLoginCredentialsDrawer
        brand={brand}
        provider={editingProvider}
        isOpen={editingProvider != null}
        onClose={() => setEditingProviderKey(null)}
        onSaved={onCredentialsSaved}
      />
      {pendingProvider != null && pendingToggle != null ? (
        <SocialLoginToggleDialog
          provider={pendingProvider}
          enable={pendingToggle.enable}
          kind={pendingToggle.kind}
          hasMultipass={hasMultipass}
          hasNewCustomerAccounts={hasNewCustomerAccounts}
          isSaving={isBrandMutationInFlight}
          onCancel={() => setPendingToggle(null)}
          onConfirm={onConfirmToggle}
          onAddCredentials={() => {
            setEditingProviderKey(pendingProvider.key);
            setPendingToggle(null);
          }}
        />
      ) : null}
    </>
  );
};

export default SocialLoginCard;
